import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import api from "@/lib/api";
import { LANDING_DEFAULTS } from "@/lib/landingDefaults";
import { Button } from "@/components/ui/button";
import HeroCalculadora from "@/components/HeroCalculadora";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  Calculator, FileCheck2, ShieldCheck, ArrowRight, Lock, LogOut,
  Check, CreditCard, Infinity as InfinityIcon, MapPin, UserPlus, FileText, Sparkles,
} from "lucide-react";

const NAVY = "#042256";
const CYAN = "#00C8C8";
const TRUST_ICONS = [CreditCard, InfinityIcon, MapPin, ShieldCheck];
const FEATURE_ICONS = [Calculator, FileCheck2, ShieldCheck];
const STEP_ICONS = [UserPlus, Calculator, FileText];

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut", delay: i * 0.1 } }),
};

// Wordmark Holistor — controlado desde el panel de admin (logo, nombre, subtítulo)
function Wordmark({ light = false, appName = "Holistor", tagline = "", logo = "" }) {
  const main = light ? "#ffffff" : NAVY;
  const sub = light ? "rgba(255,255,255,0.6)" : "#6b7280";
  return (
    <div className="flex items-center gap-2.5" data-testid="brand-logo">
      {logo ? (
        <img src={logo} alt={appName} className="h-9 w-auto max-w-[130px] object-contain" />
      ) : (
        <span className="inline-block rounded-md" style={{ width: 12, height: 12, background: CYAN }} aria-hidden />
      )}
      <div className="leading-none">
        <span className="font-head font-extrabold tracking-tight text-xl" style={{ color: main }}>{appName}</span>
        {tagline ? (
          <span className="block text-[0.5rem] font-semibold tracking-[0.22em] uppercase mt-0.5" style={{ color: sub }}>
            {tagline}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  // Contenido pre-embebido por el servidor (SSR) — evita el fetch y el "flash" de texto viejo
  const [lc, setLc] = useState(() => window.__LANDING__ || LANDING_DEFAULTS);
  const nav = useNavigate();
  const goApp = () => nav(user?.role === "admin" ? "/admin" : "/app");
  // header (fondo oscuro) → logoDark; footer (fondo claro) → logoLight
  const headerLogo = branding.logoDark || branding.logoUrl;
  const footerLogo = branding.logoLight;

  useEffect(() => {
    if (window.__LANDING__) return; // ya vino embebido desde el servidor
    api.get("/landing-content").then((r) => setLc(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ color: "#0f172a" }}>
      {/* ===== HEADER ===== */}
      <header className="sticky top-0 z-50 border-b border-white/10" style={{ background: NAVY }}>
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Wordmark light appName={branding.appName} tagline={branding.tagline} logo={headerLogo} />
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Button onClick={goApp} data-testid="header-goapp-btn" className="rounded-none gap-2 font-semibold" style={{ background: CYAN, color: NAVY }}>
                  Ir a la app <ArrowRight size={14} />
                </Button>
                <Button onClick={logout} variant="ghost" data-testid="header-logout-btn" className="rounded-none gap-2 text-white hover:bg-white/10 hover:text-white">
                  <LogOut size={14} /> Cerrar sesión
                </Button>
              </>
            ) : (
              <>
                <Link to="/admin/login">
                  <Button variant="ghost" data-testid="admin-login-link" className="rounded-none gap-2 text-white/80 hover:bg-white/10 hover:text-white">
                    <Lock size={14} /> Admin
                  </Button>
                </Link>
                <Link to="/registro">
                  <Button data-testid="header-register-btn" className="rounded-none font-semibold hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                    Ingresar
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" style={{ background: NAVY }}>
        {/* glows + grid motif */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(620px circle at 78% 32%, ${CYAN}26, transparent 60%)` }} />
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{ backgroundImage: `radial-gradient(${CYAN} 1px, transparent 1px)`, backgroundSize: "26px 26px" }} />

        <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center py-16 md:py-24">
          <motion.div className="lg:col-span-6" initial="hidden" animate="show">
            <motion.div variants={fadeUp} custom={0}
              className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 mb-6"
              style={{ background: `${CYAN}1f`, border: `1px solid ${CYAN}55` }}>
              <Sparkles size={13} style={{ color: CYAN }} />
              <span className="text-xs font-semibold tracking-wide" style={{ color: CYAN }}>{lc.hero.badge}</span>
            </motion.div>

            <motion.h1 variants={fadeUp} custom={1}
              className="font-head font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05]">
              {lc.hero.titleLine1}
              <span style={{ color: CYAN }}>{lc.hero.titleHighlight}</span>
            </motion.h1>

            <motion.p variants={fadeUp} custom={2} className="text-base md:text-lg text-white/70 mt-6 max-w-xl leading-relaxed">
              {lc.hero.subtitle}
            </motion.p>

            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap items-center gap-3 mt-9">
              {user ? (
                <Button size="lg" onClick={goApp} data-testid="hero-goapp-btn" className="rounded-none gap-2 h-12 px-8 font-bold text-base hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                  Ir a la app <ArrowRight size={18} />
                </Button>
              ) : (
                <Link to="/registro">
                  <Button size="lg" data-testid="hero-register-btn" className="rounded-none gap-2 h-12 px-8 font-bold text-base hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                    {lc.hero.ctaText} <ArrowRight size={18} />
                  </Button>
                </Link>
              )}
              <span className="text-sm text-white/55">{lc.hero.ctaNote}</span>
            </motion.div>
          </motion.div>

          <motion.div className="lg:col-span-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }}>
            <HeroCalculadora />
          </motion.div>
        </div>

        {/* trust strip */}
        <div className="relative border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 py-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            {lc.trust.map((t, idx) => {
              const Icon = TRUST_ICONS[idx % TRUST_ICONS.length];
              return (
                <div key={idx} className="flex items-center gap-2.5 text-white/75">
                  <Icon size={18} style={{ color: CYAN }} strokeWidth={1.8} />
                  <span className="text-sm font-medium">{t}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <p className="label-tiny" style={{ color: CYAN }}>{lc.features.eyebrow}</p>
          <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl mt-3 leading-tight">
            {lc.features.title}
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {lc.features.cards.map((f, idx) => {
            const Icon = FEATURE_ICONS[idx % FEATURE_ICONS.length];
            return (
            <motion.div key={idx} variants={fadeUp} custom={idx} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              className="group relative bg-white border border-slate-200 p-7 transition-all hover:border-transparent hover:shadow-[0_24px_60px_-20px_rgba(4,34,86,0.25)]">
              <div className="absolute top-0 left-0 h-1 w-0 group-hover:w-full transition-all duration-300" style={{ background: CYAN }} />
              <div className="flex items-center justify-center w-12 h-12 rounded-xl mb-5" style={{ background: `${NAVY}0d` }}>
                <Icon size={24} strokeWidth={1.6} style={{ color: NAVY }} />
              </div>
              <h3 className="font-head font-bold text-slate-900 text-lg">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </motion.div>
            );
          })}
        </div>
      </section>

      {/* ===== CÓMO FUNCIONA ===== */}
      <section style={{ background: "#f7f9fc" }} className="border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-28">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="label-tiny" style={{ color: CYAN }}>{lc.howItWorks.eyebrow}</p>
            <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl mt-3">{lc.howItWorks.title}</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8 relative">
            {lc.howItWorks.steps.map((s, idx) => {
              const Icon = STEP_ICONS[idx % STEP_ICONS.length];
              const n = String(idx + 1).padStart(2, "0");
              return (
              <motion.div key={idx} variants={fadeUp} custom={idx} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
                className="relative bg-white border border-slate-200 p-7">
                <span className="font-head font-extrabold text-5xl" style={{ color: `${NAVY}14` }}>{n}</span>
                <div className="flex items-center justify-center w-11 h-11 rounded-xl -mt-3 mb-4" style={{ background: NAVY }}>
                  <Icon size={20} strokeWidth={1.7} className="text-white" />
                </div>
                <h3 className="font-head font-bold text-slate-900 text-lg">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== POR QUÉ GRATIS ===== */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="label-tiny" style={{ color: CYAN }}>{lc.whyFree.eyebrow}</p>
          <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl mt-3 leading-tight">
            {lc.whyFree.title}
          </h2>
          <p className="text-slate-600 mt-5 leading-relaxed">
            {lc.whyFree.paragraph}
          </p>
          <ul className="mt-7 space-y-3">
            {lc.whyFree.bullets.map((t, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span className="mt-0.5 flex items-center justify-center w-5 h-5 rounded-full shrink-0" style={{ background: CYAN }}>
                  <Check size={12} strokeWidth={3} style={{ color: NAVY }} />
                </span>
                <span className="text-sm text-slate-700">{t}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <div className="rounded-2xl p-8 md:p-10 text-white relative overflow-hidden" style={{ background: NAVY }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(400px circle at 80% 10%, ${CYAN}22, transparent 60%)` }} />
            <p className="font-head font-extrabold text-6xl md:text-7xl" style={{ color: CYAN }}>{lc.whyFree.priceBig}</p>
            <p className="text-white/80 mt-2 text-lg font-semibold">{lc.whyFree.priceSub}</p>
            <p className="text-white/55 text-sm mt-3 leading-relaxed">
              {lc.whyFree.priceParagraph}
            </p>
            <div className="mt-7">
              {user ? (
                <Button onClick={goApp} className="rounded-none gap-2 font-bold hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                  Ir a la app <ArrowRight size={16} />
                </Button>
              ) : (
                <Link to="/registro">
                  <Button className="rounded-none gap-2 font-bold hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                    {lc.whyFree.priceCta} <ArrowRight size={16} />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== PREGUNTAS FRECUENTES ===== */}
      {lc.faq?.items?.length > 0 && (
        <section style={{ background: "#f7f9fc" }} className="border-t border-slate-200" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-6 py-20 md:py-28">
            <div className="text-center mb-12">
              <p className="label-tiny" style={{ color: CYAN }}>{lc.faq.eyebrow}</p>
              <h2 className="font-head font-extrabold text-slate-900 text-3xl md:text-4xl mt-3">{lc.faq.title}</h2>
            </div>
            <Accordion type="single" collapsible className="bg-white border border-slate-200">
              {lc.faq.items.map((it, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="px-6" data-testid={`faq-item-${idx}`}>
                  <AccordionTrigger className="font-head font-bold text-slate-900 text-left text-base hover:no-underline">
                    {it.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                    {it.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* ===== CTA FINAL ===== */}
      <section style={{ background: NAVY }} className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{ backgroundImage: `radial-gradient(${CYAN} 1px, transparent 1px)`, backgroundSize: "26px 26px" }} />
        <div className="relative max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="font-head font-extrabold text-white text-3xl md:text-5xl leading-tight">
            {lc.finalCta.title}
          </h2>
          <p className="text-white/65 mt-5 max-w-xl mx-auto">
            {lc.finalCta.paragraph}
          </p>
          <div className="mt-9 flex justify-center">
            {user ? (
              <Button size="lg" onClick={goApp} className="rounded-none gap-2 h-12 px-9 font-bold text-base hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                Ir a la app <ArrowRight size={18} />
              </Button>
            ) : (
              <Link to="/registro">
                <Button size="lg" className="rounded-none gap-2 h-12 px-9 font-bold text-base hover:opacity-90" style={{ background: CYAN, color: NAVY }}>
                  {lc.finalCta.ctaText} <ArrowRight size={18} />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Wordmark appName={branding.appName} tagline={branding.tagline} logo={footerLogo} />
          <p className="text-sm text-slate-500 text-center sm:text-right" data-testid="footer-text">
            {branding.footerText}
          </p>
        </div>
      </footer>
    </div>
  );
}
